import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Radio, Space, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Select from 'components/Select';
import LinkPicker from 'components/LinkOmniPicker/LinkPicker';
import OmniPicker from 'components/OmniPicker';
import CSVDownload from 'components/CSVDownload';
import { thunkFetchChecklists } from '@/redux/actions';
import { checklistSize } from 'components/ChecklistEditor/checklistDraft';
import WidgetOptionGroupPermissions from './WidgetOptionGroupPermissions';
import './ChecklistWidgetOptions.scss';

const SHOW_CHOICES = [
	{
		value: 'current',
		label: "Only what's current",
		hint: 'Good for the homescreen. Past and upcoming sections are left out.',
	},
	{
		value: 'all',
		label: 'Every section that has shown up',
		hint: 'Good for a full checklist page. Past sections are collapsed.',
	},
];

const ChecklistWidgetOptions = props => {
	const {
		event,
		widgetProps,
		handlePropChange,
		openChecklistEditor,
		handleGroupChange,
		targetKeys,
		checklists,
		loaded,
		loadFailed,
		fetchChecklists,
	} = props;
	const { checklist_id, show, full_checklist_on_press } = widgetProps;
	const [pickingLink, setPickingLink] = useState(false);
	// A widget can point at a checklist that was deleted since it was saved.
	const selected = checklists.find(checklist => checklist.id === checklist_id);
	const setChecklistID = handlePropChange('checklist_id');
	const setLink = handlePropChange('full_checklist_on_press');
	// The editor stays open across saves, so its callbacks must not close over this render.
	const latest = useRef();
	latest.current = { checklistID: checklist_id, handlePropChange };

	const openEditor = checklistID =>
		openChecklistEditor({
			checklistID,
			onSaved: saved => latest.current.handlePropChange('checklist_id')(saved.id),
			onDeleted: deletedID => {
				if (deletedID === latest.current.checklistID)
					latest.current.handlePropChange('checklist_id')(null);
			},
		});

	return (
		<>
			<div className="WidgetUpdateForm ChecklistWidgetOptions">
				<h4 className="admin-form-item-label">Checklist</h4>
				{!loaded ? (
					<div className="ChecklistWidgetOptions-box">
						<Spin />
					</div>
				) : loadFailed && checklists.length === 0 ? (
					<div className="ChecklistWidgetOptions-box">
						<p>Couldn't load this event's checklists.</p>
						<Button onClick={() => fetchChecklists(event.id)}>Try again</Button>
					</div>
				) : checklists.length === 0 ? (
					<div className="ChecklistWidgetOptions-box">
						<p>This event has no checklists yet.</p>
						<Button type="primary" onClick={() => openEditor(null)}>
							Create checklist
						</Button>
					</div>
				) : (
					<div className="ChecklistWidgetOptions-box">
						<div className="ChecklistWidgetOptions-picker">
							<Select
								data={checklists.map(({ id, name }) => ({ id, name }))}
								value={selected ? [selected.id] : []}
								onChange={setChecklistID}
								form={true}
							/>
							{selected && (
								<p className="ChecklistWidgetOptions-size">{checklistSize(selected.content)}</p>
							)}
						</div>
						<Space>
							<Button
								type="primary"
								size="small"
								disabled={!selected}
								onClick={() => openEditor(selected.id)}
							>
								Edit checklist
							</Button>
							<CSVDownload
								name="Checklist"
								text="Download completions"
								antdSize="small"
								fileName={selected?.name}
								checklistID={selected?.id}
								disabled={!selected}
							/>
						</Space>
						<Button
							type="link"
							size="small"
							icon={<PlusOutlined />}
							onClick={() => openEditor(null)}
						>
							Create a new checklist
						</Button>
					</div>
				)}
				<h4 className="admin-form-item-label">Which sections</h4>
				<Radio.Group
					className="ChecklistWidgetOptions-show"
					value={show ?? 'current'}
					onChange={e => handlePropChange('show')(e.target.value)}
				>
					{SHOW_CHOICES.map(({ value, label, hint }) => (
						<Radio key={value} value={value}>
							{label}
							<small>{hint}</small>
						</Radio>
					))}
				</Radio.Group>
				<h4 className="admin-form-item-label">Link to the full checklist</h4>
				<LinkPicker
					onClick={() => setPickingLink(true)}
					onPress={full_checklist_on_press}
					placeholderStr="Add link"
					onConfirm={() => setLink({})}
				/>
				<WidgetOptionGroupPermissions
					event={event}
					handleGroupChange={handleGroupChange}
					targetKeys={targetKeys}
				/>
			</div>
			<OmniPicker
				event={event}
				visible={pickingLink}
				onCancel={() => setPickingLink(false)}
				handlePickItem={link => {
					setPickingLink(false);
					setLink(link);
				}}
				onPress={full_checklist_on_press}
				linkPicker={event.single_page_app !== 'TRUE'}
				from="checklist"
			/>
		</>
	);
};

const msp = ({ checklists }) => ({
	checklists: checklists.checklists,
	loaded: checklists.loaded,
	loadFailed: checklists.loadFailed,
});

const mdp = dispatch => ({
	fetchChecklists: eventID => dispatch(thunkFetchChecklists(eventID)),
});

export default connect(msp, mdp)(ChecklistWidgetOptions);
